import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toUiLanguage, UI_LANGUAGE_METADATA_KEY } from '@unipostudio/shared-types';
import { useAuthStore } from '@/shared/stores/auth.store';
import { profileService } from '@/shared/services/profile.service';
import { applyLanguage, hasExplicitLanguageChoice } from '@/shared/i18n/useLanguage';

/**
 * Keeps the signed-in user's interface language (user_metadata.ui_language)
 * and the one on screen in step, so the emails and notifications the worker
 * writes are in the reader's language.
 *
 * - The account's language wins over one merely detected from the browser:
 *   on a new device, in a private window or after storage was cleared the
 *   detector picks the browser language (often English), and writing that
 *   back silently switched a Malayalam or Arabic reader's emails to English.
 *   Such a session is switched to the account's language instead.
 * - A language the person chose on this device (switcher, onboarding,
 *   landing) is written to the account, once per value; a failed write is
 *   retried at the next change and never interrupts the person.
 * - An account with no language yet takes the one on screen.
 */
export function useSyncUiLanguage(): void {
  const { i18n } = useTranslation();
  const userId = useAuthStore((s) => s.user?.id);
  const stored = useAuthStore((s) => toUiLanguage(s.user?.user_metadata?.[UI_LANGUAGE_METADATA_KEY]));
  const current = toUiLanguage(i18n.language);
  const sent = useRef<string | null>(null);

  useEffect(() => {
    if (!userId || !current || current === stored) return;
    if (stored && !hasExplicitLanguageChoice()) {
      void applyLanguage(i18n, stored);
      return;
    }
    if (sent.current === `${userId}:${current}`) return;
    sent.current = `${userId}:${current}`;
    profileService.updateLanguage(current).catch(() => {
      sent.current = null;
    });
  }, [userId, current, stored, i18n]);
}
